import React, { useContext, useEffect, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { IReviewAndPayWhenToSendStepModel, ReviewAndPayWhenToSendStepModel } from "../../../../models/buy-giftcard/review-pay/ReviewAndPayWhenToSendStep.model";
import { BuyGiftCardStateEnum } from "../../../../models/enums/BuyGiftCardStateEnum";
import { IBuyGiftCardStepProps } from "../../../../models/interfaces/BuyGiftCardStep.interface";
import { AppSettingContext } from "../../../Application";
import LoadingComponent from "../../../shared/LoadingComponent";

interface IReviewAndPayWhenToSendStepProps extends IBuyGiftCardStepProps {
    stepModel: IReviewAndPayWhenToSendStepModel;
    isAvailable: boolean;

    handleSubmit(stepModel: IReviewAndPayWhenToSendStepModel): void;
}

const ReviewAndPayWhenToSendStep = (props: IReviewAndPayWhenToSendStepProps) => {
    const { labels } = useContext(AppSettingContext);
    const checkboxRef = useRef(null);

    //#region STATE PROPERTIES

    const [stepModel, setStepModel] = useState<IReviewAndPayWhenToSendStepModel>({
        sendLater: props.stepModel.sendLater,
        scheduleSendDetails: {
            date: props.stepModel.scheduleSendDetails?.date ?? new Date(),
            hour: props.stepModel.scheduleSendDetails?.hour ?? (new Date()).getHours(),
            minute: props.stepModel.scheduleSendDetails?.minute ?? (new Date()).getMinutes()
        }
    });
    const [isLoading, setIsLoading] = useState(true);
    const [isModelValid, setIsModelValid] = useState<boolean>();

    //#endregion

    //#region USE EFFECT HOOKS

    useEffect(() => {
        setIsModelValid(isValid());
    }, [stepModel]);

    useEffect(() => {
        setIsLoading(props.isSaving);
    }, [props.isSaving]);

    useEffect(() => {
        checkboxRef.current.checked = props.activeStep === BuyGiftCardStateEnum.REVIEW_PAY_WHEN_TO_SEND;
    }, [props.activeStep]);

    //#endregion

    //#region HANDLERS

    const handleExpandBoxCheck = (): void => {
        if (props.isAvailable) {
            if (checkboxRef.current.checked && props.activeStep !== BuyGiftCardStateEnum.REVIEW_PAY_WHEN_TO_SEND) {
                props.handleSetActiveStep(BuyGiftCardStateEnum.REVIEW_PAY_WHEN_TO_SEND);
            }
        }
    };

    const handleSendLaterChange = (sendLater: boolean): void => {
        setStepModel({
            ...stepModel,
            sendLater: sendLater
        });
    };

    const handleDateChange = (newDate: Date): void => {
        setStepModel({
            ...stepModel,
            scheduleSendDetails: {
                ...stepModel.scheduleSendDetails,
                date: newDate
            }
        });
    };

    const handleTimeChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        const { name, value } = event.target;

        setStepModel({
            ...stepModel,
            scheduleSendDetails: {
                ...stepModel.scheduleSendDetails,
                [name]: parseInt(value)
            }
        });
    };

    const handleSaveAndContinue = (event: React.MouseEvent<HTMLButtonElement>): void => {
        event.preventDefault();

        if (!isValid()) {
            return;
        }

        props.handleSubmit(stepModel);
        checkboxRef.current.checked = false;
    };

    //#endregion

    //#region COMMON HELPERS

    const addDays = (date: Date, numberOfDaysToAdd: number) => {
        if (date) {
            date.setDate(date.getDate() + numberOfDaysToAdd);
        }

        return date;
    };

    const isDateSame = (date: Date) => {
        const today = new Date();

        if (date) {
            return date.getDate() == today.getDate()
                && date.getMonth() == today.getMonth()
                && date.getFullYear() == today.getFullYear();
        }

        return false;
    }

    const isValid = (): boolean => {
        return ReviewAndPayWhenToSendStepModel.isValid(stepModel);
    };

    //#endregion

    return (
        <div className="box">
            <input
                type="checkbox"
                name="when-send-it"
                id="when-send-it"
                ref={checkboxRef}
                onChange={handleExpandBoxCheck}
                defaultChecked={false}
            />
            <div className="action-card">
                <h4 className="box-heading">
                    <i className="box-icon">
                        <img src="/static/options-card-page/options-card-icon-08.png" alt="icon 08" />
                    </i>
                    {labels.get("GiftCard.App.V2.ReviewAndPayWhenToSend.Title")}
                    <i
                        className="box-arrow"
                        style={{ cursor: props.isAvailable ? "pointer" : "default" }}
                        onClick={() => {
                            if (props.isAvailable) {
                                checkboxRef.current.checked = !checkboxRef.current.checked;
                                handleExpandBoxCheck();
                            }
                        }}
                    ></i>
                </h4>
                {props.activeStep === BuyGiftCardStateEnum.REVIEW_PAY_WHEN_TO_SEND && (
                    <div className="show" style={{ overflow: `${stepModel.sendLater ? "visible" : "hidden"}` }}>
                        {isLoading ? (
                            <LoadingComponent />
                        ) : (
                            <div className="slide-content" style={{ transition: `${stepModel.sendLater ? "none" : "visibility 1s"}` }}>
                                <div className="send-options">
                                    <ul>
                                        <li>
                                            <input
                                                type="radio"
                                                name="send-options"
                                                id="send-now"
                                                checked={!stepModel.sendLater}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        handleSendLaterChange(false);
                                                    }
                                                }}
                                            />
                                            <label htmlFor="send-now">{labels.get("GiftCard.App.V2.ReviewAndPayWhenToSend.Label.SendNow")}</label>
                                        </li>
                                        <li>
                                            <input
                                                type="radio"
                                                name="send-options"
                                                id="send-later"
                                                style={{ transition: stepModel.sendLater ? "none" : ".3s ease" }}
                                                checked={stepModel.sendLater}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        handleSendLaterChange(true);
                                                    }
                                                }}
                                            />
                                            <label htmlFor="send-later">{labels.get("GiftCard.App.V2.ReviewAndPayWhenToSend.Label.SendLater")}</label>
                                        </li>
                                    </ul>

                                    {stepModel.sendLater && (
                                        <div className={`flex-container date ${stepModel.sendLater ? "" : "date-hide fadeIn"}`}>
                                            <div className="form-field">
                                                <label htmlFor="send-it__date">Date</label>
                                                <div className="date-picker" style={{ position: 'relative' }}>
                                                    <DatePicker
                                                        id="send-it__date" name="send-it__date"
                                                        showIcon
                                                        dateFormat="dd MMMM yyyy"
                                                        calendarClassName=""
                                                        className="send-it__date-picker"
                                                        placeholderText=''
                                                        minDate={new Date()}
                                                        maxDate={addDays(new Date(), 120)}
                                                        closeOnScroll={false}
                                                        selected={stepModel.scheduleSendDetails.date}
                                                        onChange={handleDateChange}
                                                        calendarStartDay={1}
                                                    />

                                                    <img src="/static/icons/date-icon.svg" alt="Date" />
                                                    <img src="/static/icons/input-select-icon.svg" alt="Select" />
                                                </div>
                                            </div>
                                            <div className="form-field">
                                                <label htmlFor="send-it__hour">Hour</label>
                                                <div className="select-field">
                                                    <select
                                                        id="hour"
                                                        name="hour"
                                                        value={stepModel.scheduleSendDetails.hour}
                                                        onChange={handleTimeChange}
                                                    >
                                                        {(() => {
                                                            const options = [];

                                                            for (let i = 0; i <= 23; i++) {
                                                                if (isDateSame(stepModel.scheduleSendDetails.date) &&
                                                                    (i < stepModel.scheduleSendDetails.date.getHours())) {
                                                                    continue;
                                                                }

                                                                options.push(
                                                                    <option key={i} value={i}>{i.toString().padStart(2, '0')}</option>
                                                                );
                                                            }

                                                            return options;
                                                        })()}
                                                    </select>
                                                    <img src="/static/icons/input-select-icon.svg" alt="Select" />
                                                </div>
                                            </div>
                                            <div className="form-field">
                                                <label htmlFor="send-it_minute">Minute</label>
                                                <div className="select-field">
                                                    <select
                                                        id="minute"
                                                        name="minute"
                                                        value={stepModel.scheduleSendDetails.minute}
                                                        onChange={handleTimeChange}
                                                    >
                                                        {(() => {
                                                            const options = [];

                                                            for (let i = 0; i <= 59; i++) {
                                                                options.push(
                                                                    <option key={i} value={i}>{i.toString().padStart(2, '0')}</option>
                                                                );
                                                            }

                                                            return options;
                                                        })()}
                                                    </select>
                                                    <img src="/static/icons/input-select-icon.svg" alt="Select" />
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div className="buttons">
                                    <button
                                        className="btn btn__full_width btn__primary large-fs btn__large"
                                        onClick={handleSaveAndContinue}
                                        disabled={!isModelValid}
                                    >
                                        {labels.get("GiftCard.App.V2.ReviewAndPayWhenToSend.Button.Submit")}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ReviewAndPayWhenToSendStep;